import { DateTime as DT } from 'luxon';
import type {
    LeadType,
    ObjectType,
    TaskCreateDataType,
    TaskCreateFormType,
    TaskForType,
    UserType,
} from '@econtractor/types';

export type { TaskCreateFormType };

export const resolveTaskFor = (
    leadId: number | null = null,
    objectId: number | null = null,
): TaskForType => {
    if (objectId) return 'object';
    if (leadId) return 'lead';
    return 'personal';
};

export const getInitNewTaskStruct = (
    leadId: number | null = null,
    objectId: number | null = null,
    currentUser: UserType | null = null,
    lead: LeadType | null = null,
    object: ObjectType | null = null,
): TaskCreateFormType => {
    const today = DT.now().setZone('local');
    const taskFor = resolveTaskFor(leadId, objectId);
    return {
        taskFor,
        lead: taskFor === 'lead' ? lead : null,
        object: taskFor === 'object' ? object : null,
        taskTypeId: null,
        taskName: '',
        taskInfo: '',
        executor: null,
        controller: currentUser,
        multiDay: false,
        withTime: false,
        dateStart: today.startOf('day').toMillis(),
        dateEnd: today.endOf('day').toMillis(),
        endTime: '18:00',
    };
};

/** Початок доби для ISO-дати */
export const isoDateToStartMillis = (isoDate: string): number | null => {
    const raw = String(isoDate || '').trim();
    if (!raw) return null;
    const parsed = DT.fromISO(raw, { zone: 'local' }).startOf('day');
    return parsed.isValid ? parsed.toMillis() : null;
};

/** Кінець доби або дата+час (HH:mm) для date_end */
export const isoDateToEndMillis = (
    isoDate: string,
    withTime = false,
    time = '18:00',
): number | null => {
    const raw = String(isoDate || '').trim();
    if (!raw) return null;
    const day = DT.fromISO(raw, { zone: 'local' });
    if (!day.isValid) return null;
    // без галочки «Вказати час» — кінець доби
    if (!withTime) return day.endOf('day').toMillis();
    // з галочкою — саме вказаний час цієї доби
    const [hh, mm] = String(time || '18:00').split(':').map((v) => Number(v));
    const withClock = day.set({
        hour: Number.isFinite(hh) ? hh : 18,
        minute: Number.isFinite(mm) ? mm : 0,
        second: 0,
        millisecond: 0,
    });
    return withClock.isValid ? withClock.toMillis() : day.endOf('day').toMillis();
};

/**
 * Перерахунок dateEnd з millis дати + withTime + endTime.
 * Бізнес-значення дедлайну — саме dateEnd; endTime лише джерело годин:хвилин при записі.
 */
export const resolveDateEndMillis = (
    dateEndMillis: number,
    withTime: boolean,
    endTime: string | null | undefined,
): number => {
    const endDateIso = millisToIsoDate(dateEndMillis);
    return (
        isoDateToEndMillis(endDateIso, withTime, endTime || '18:00') ||
        dateEndMillis
    );
};

export const millisToIsoDate = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '';
    return DT.fromMillis(value, { zone: 'local' }).toISODate() || '';
};

export const millisToTime = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '18:00';
    return DT.fromMillis(value, { zone: 'local' }).toFormat('HH:mm');
};

/**
 * Нормалізація перед сабмітом.
 * dateEnd — авторитетний дедлайн (кінець доби або дата+час).
 * endTime — лише інформаційне поле для БД (HH:mm), у порівняннях/термінах не використовується.
 */
export const normalizeNewTaskValues = (values: TaskCreateFormType): TaskCreateDataType => {
    const dateEnd = resolveDateEndMillis(values.dateEnd, values.withTime, values.endTime);
    const dateStart = values.multiDay
        ? DT.fromMillis(values.dateStart, { zone: 'local' }).startOf('day').toMillis()
        : DT.fromMillis(dateEnd, { zone: 'local' }).startOf('day').toMillis();

    const leadId =
        values.taskFor === 'lead' ? (values.lead?.ID ? Number(values.lead.ID) : null) : null;
    const objectId =
        values.taskFor === 'object'
            ? values.object?.ID
                ? Number(values.object.ID)
                : null
            : null;

    return {
        leadId,
        objectId,
        taskTypeId: values.taskTypeId,
        taskName: values.taskName,
        taskInfo: values.taskInfo,
        executorId: values.executor?.ID ?? null,
        controllerId: values.controller?.ID ?? null,
        multiDay: values.multiDay,
        withTime: values.withTime,
        dateStart,
        dateEnd,
        endTime: values.withTime ? String(values.endTime || '18:00').trim() || null : null,
    };
};
